import React, { useState, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { classNames } from "primereact/utils";
import { LawyerData } from "../../types/types";
import {
  getCompanyDataByCVR,
  getCompanyDataByName,
  formatDetailedCVRdata,
} from "../../Helpers/cvr";
import { getDarById, formatDetailedDARdata } from "../../Helpers/dar";
import { getBbrById, formatDetailedBBRdata } from "../../Helpers/bbr";

interface LawyerTemplateProps {
  visible: boolean;
  onHide: () => void;
  onCreateCase: (data: LawyerData) => void;
}

const LawyerTemplate: React.FC<LawyerTemplateProps> = ({
  visible,
  onHide,
  onCreateCase,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [cvrChecked, setCvrChecked] = useState(false);
  const [darChecked, setDarChecked] = useState(false);
  const [bbrChecked, setBbrChecked] = useState(false);
  const [svrChecked, setSvrChecked] = useState(false);
  const [ejfChecked, setEjfChecked] = useState(false);
  const [vurChecked, setVurChecked] = useState(false);
  const [cprChecked, setCprChecked] = useState(false);
  const [cvrData, setCvrData] = useState<any>(null);
  const [darData, setDarData] = useState<any>(null);
  const [bbrData, setBbrData] = useState<any>(null);
  const [svrData, setSvrData] = useState<any>(null);
  const [ejfData, setEjfData] = useState<any>(null);
  const [vurData, setVurData] = useState<any>(null);
  const [cprData, setCprData] = useState<any>(null);
  const [formattedCvrData, setFormattedCvrData] = useState<any>(null);
  const [formattedDarData, setFormattedDarData] = useState<any>(null);
  const [formattedBbrData, setFormattedBbrData] = useState<any>(null);
  const [formattedSvrData, setFormattedSvrData] = useState<any>(null);
  const [formattedEjfData, setFormattedEjfData] = useState<any>(null);
  const [formattedVurData, setFormattedVurData] = useState<any>(null);
  const [formattedCprData, setFormattedCprData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCvrError, setIsCvrError] = useState(false);
  const [isDarError, setIsDarError] = useState(false);
  const [isBbrError, setIsBbrError] = useState(false);
  const [isSvrError, setIsSvrError] = useState(false);
  const [isEjfError, setIsEjfError] = useState(false);
  const [isVurError, setIsVurError] = useState(false);
  const [isCprError, setIsCprError] = useState(false);
  const [success, setSuccess] = useState(false);

  const [cvrInfoVisible, setCvrInfoVisible] = useState(false);
  const [darInfoVisible, setDarInfoVisible] = useState(false);
  const [bbrInfoVisible, setBbrInfoVisible] = useState(false);
  const [svrInfoVisible, setSvrInfoVisible] = useState(false);
  const [ejfInfoVisible, setEjfInfoVisible] = useState(false);
  const [vurInfoVisible, setVurInfoVisible] = useState(false);
  const [cprInfoVisible, setCprInfoVisible] = useState(false);

  const darAddressId = useRef<string | null>(null);

  const fetchCVR = async () => {
    try {
      setError("");
      if (inputValue.trim() === "") {
        setError("Please enter a CVR number or name.");
        return { success: false };
      }
      setLoading(true);
      let nameResult: any[] = [];
      if (isNaN(Number(inputValue))) {
        nameResult = await getCompanyDataByName(inputValue);
        if (!nameResult || nameResult.length === 0) {
          setError("No company found with the provided name.");
          setLoading(false);
          return { success: false };
        }
        if (nameResult.length > 1) {
          setCvrData(JSON.stringify(nameResult));
          //setCvrInfoVisible(true);
          setLoading(false);
          setError("Multiple companies found. Please specify further.");
          return { success: false };
        }
      }
      const cvrResult = await getCompanyDataByCVR(
        !isNaN(Number(inputValue))
          ? inputValue
          : nameResult[0].CVRNummer.toString()
      );
      setLoading(false);
      if (cvrResult) {
        setCvrData(cvrResult);
        const formattedResult = formatDetailedCVRdata(cvrResult);
        setFormattedCvrData(formattedResult);
        darAddressId.current = cvrResult?.beliggenhedsadresse.adresse;
        return { success: true };
      } else {
        setError("No CVR data found for the specified number.");
        return { success: false };
      }
      setLoading(false);
    } catch (error) {
      setError("Error fetching CVR data");
      setLoading(false);
      return { success: false };
    }
  };

  const fetchDAR = async () => {
    try {
      if (!darAddressId.current) {
        setError("No address found for the specified CVR number.");
        return { success: false };
      }
      const darResult = await getDarById(darAddressId.current);
      if (!darResult) {
        setError("No address found for the specified CVR address id.");
        setLoading(false);
        return { success: false };
      }

      if (darResult) {
        setDarData(darResult);
        const formattedResult = formatDetailedDARdata(darResult);
        setDarData(formattedResult);
        setFormattedDarData(formattedResult);
        return { success: true };
      }
    } catch (error) {
      setError("Error fetching DAR data");
      setLoading(false);
      return { success: false };
    }
  };

  const fetchBBR = async () => {
    try {
      if (!darAddressId.current) {
        setError("No address found for the specified CVR number.");
        return { success: false };
      }
      const bbrResult = await getBbrById(darAddressId.current);
      if (!bbrResult) {
        setError("No address found for the specified CVR address id.");
        setLoading(false);
        return { success: false };
      }

      if (bbrResult) {
        setBbrData(bbrResult);
        const formattedResult = formatDetailedBBRdata(bbrResult);
        setBbrData(formattedResult);
        setFormattedBbrData(formattedResult);
        return { success: true };
      }
    } catch (error) {
      setError("Error fetching BBR data");
      setLoading(false);
      return { success: false };
    }
  };

  const handleFetchData = async () => {
    setCvrData(null);
    setDarData(null);
    setBbrData(null);
    setCvrChecked(false);
    setDarChecked(false);
    setBbrChecked(false);
    setIsCvrError(false);
    setIsDarError(false);
    setIsBbrError(false);

    const cvrResponse = (await fetchCVR()) as { success: boolean };
    if (cvrResponse.success) {
      setCvrChecked(true);
    } else {
      setIsCvrError(true);
      return;
    }

    const darResponse = (await fetchDAR()) as { success: boolean };
    if (darResponse.success) {
      setDarChecked(true);
    } else {
      setIsDarError(true);
      return;
    }

    const bbrResponse = (await fetchBBR()) as { success: boolean };
    if (bbrResponse.success) {
      setBbrChecked(true);
    } else {
      setIsBbrError(true);
      return;
    }
  };

  return (
    <Dialog
      header="Business Module for Law Firms"
      contentStyle={{ overflow: "hidden" }}
      visible={visible}
      style={{ width: "50vw", height: "60vh" }}
      onHide={onHide}
    >
      <div
        className="p-field"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          verticalAlign: "middle",
        }}
      >
        <label
          htmlFor="input"
          style={{ marginBottom: "10px", textAlign: "center", height: "10px" }}
        >
          Indhent data:&nbsp;
        </label>
        <InputText
          id="input"
          placeholder="angiv navn eller cvr nr."
          value={inputValue}
          style={{
            maxWidth: "300px",
            minWidth: "250px",
            fontSize: "14px",
            textAlign: "left",
            whiteSpace: "nowrap",
          }}
          onChange={(e) => setInputValue(e.target.value)}
        />
      </div>
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "30px" }}
      >
        <Button
          label="Hent Data"
          onClick={handleFetchData}
          className="p-mt-20"
        />
      </div>

      <div
        className="p-mt-4"
        style={{ justifySelf: "center", width: "310px", marginTop: "30px" }}
      >
        <div className="p-d-flex p-ai-center" style={{ marginTop: "10px" }}>
          <Checkbox
            style={{ marginRight: "10px" }}
            checked={cvrChecked}
            className={classNames({ "p-checkbox-checked": cvrChecked })}
          />
          <span className="p-ml-2">CVR - Virksomhedsregister</span>
          {(cvrChecked || isCvrError) && (
            <i
              className="pi pi-info-circle"
              style={{
                marginLeft: "10px",
                color: isCvrError ? "red" : "inherit",
              }}
              onClick={() => {
                setCvrInfoVisible(true);
              }}
            />
          )}
        </div>
        <div className="p-d-flex p-ai-center" style={{ marginTop: "10px" }}>
          <Checkbox
            style={{ marginRight: "10px" }}
            checked={darChecked}
            className={classNames({ "p-checkbox-checked": darChecked })}
          />
          <span className="p-ml-2">DAR - Adresseregister</span>
          {darChecked && (
            <i
              className="pi pi-info-circle"
              style={{ marginLeft: "10px" }}
              onClick={() => {
                setDarInfoVisible(true);
              }}
            />
          )}
        </div>
        <div className="p-d-flex p-ai-center" style={{ marginTop: "10px" }}>
          <Checkbox
            style={{ marginRight: "10px" }}
            checked={bbrChecked}
            className={classNames({ "p-checkbox-checked": bbrChecked })}
          />
          <span className="p-ml-2">BBR - Bygnings- og Boligregister</span>
          {bbrChecked && (
            <i
              className="pi pi-info-circle"
              style={{ marginLeft: "10px" }}
              onClick={() => {
                setBbrInfoVisible(true);
              }}
            />
          )}
        </div>
        <div className="p-d-flex p-ai-center" style={{ marginTop: "10px" }}>
          <Checkbox
            style={{ marginRight: "10px" }}
            checked={ejfChecked}
            className={classNames({ "p-checkbox-checked": ejfChecked })}
          />
          <span className="p-ml-2">SVR - SKAT's virksomhedsregister</span>
          {ejfChecked && (
            <i className="pi pi-info-circle" style={{ marginLeft: "10px" }} />
          )}
        </div>
        <div className="p-d-flex p-ai-center" style={{ marginTop: "10px" }}>
          <Checkbox
            style={{ marginRight: "10px" }}
            checked={ejfChecked}
            className={classNames({ "p-checkbox-checked": ejfChecked })}
          />
          <span className="p-ml-2">EJF - Ejerfortegnelsen</span>
          {ejfChecked && (
            <i className="pi pi-info-circle" style={{ marginLeft: "10px" }} />
          )}
        </div>
        <div className="p-d-flex p-ai-center" style={{ marginTop: "10px" }}>
          <Checkbox
            style={{ marginRight: "10px" }}
            checked={vurChecked}
            className={classNames({ "p-checkbox-checked": vurChecked })}
          />
          <span className="p-ml-2">VUR - Ejendomsvurderingen</span>
          {vurChecked && (
            <i className="pi pi-info-circle" style={{ marginLeft: "10px" }} />
          )}
        </div>
        <div className="p-d-flex p-ai-center" style={{ marginTop: "10px" }}>
          <Checkbox
            style={{ marginRight: "10px" }}
            checked={vurChecked}
            className={classNames({ "p-checkbox-checked": cprChecked })}
          />
          <span className="p-ml-2">CPR - Personregister</span>
          {cprChecked && (
            <i className="pi pi-info-circle" style={{ marginLeft: "10px" }} />
          )}
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          bottom: "20px",
          right: "20px",
        }}
      >
        <Button
          label="Opret sagsoverblik"
          onClick={() => {
            onCreateCase({
              name: inputValue,
              cvrData,
              darData,
              bbrData,
              svrData,
              ejfData,
              vurData,
              cprData,
              formattedBbrData,
              formattedCvrData,
              formattedDarData,
              formattedSvrData,
              formattedEjfData,
              formattedVurData,
              formattedCprData,
            });
          }}
          className="p-mt-10"
          disabled={!cvrChecked || !darChecked || !bbrChecked}
        />
      </div>

      <>
        <Dialog
          header="Virksomhedsregister Data"
          visible={cvrInfoVisible}
          style={{ width: "50vw" }}
          onHide={() => {
            if (!cvrInfoVisible) return;
            setCvrInfoVisible(false);
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: formattedCvrData }} />
        </Dialog>

        <Dialog
          header="Adresseregister Data"
          visible={darInfoVisible}
          style={{ width: "50vw" }}
          onHide={() => {
            if (!darInfoVisible) return;
            setDarInfoVisible(false);
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: formattedDarData }} />
        </Dialog>

        <Dialog
          header="Bygnings- og Boligregister Data"
          visible={bbrInfoVisible}
          style={{ width: "50vw" }}
          onHide={() => {
            if (!bbrInfoVisible) return;
            setBbrInfoVisible(false);
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: formattedBbrData }} />
        </Dialog>

        <div
          style={{
            display: "flex",
            justifyContent: "left",
            marginTop: "-10px",
          }}
        >
          {error && (
            <div
              style={{
                position: "absolute",
                bottom: "20px",
                left: "10px",
                color: "red",
                fontSize: "14px",
              }}
            >
              {error}
            </div>
          )}
        </div>
      </>
    </Dialog>
  );
};

export default LawyerTemplate;
